import type { GetExploreItemsResponse, GetExploreNearbyItemsResponse } from './types'
import { baseUri, getOptions } from '../utils'

import request from 'api/requestUtils'
import { onlyDefinedProperties } from 'lib/object/objectUtils'

const endpointUri = `${baseUri}/v2/explore`
const nearbyEndpointUri = `${endpointUri}/nearby`

export type ExploreParams = {
  query: string
  locationBias: [number, number]
  /** Type of place to search for, as per https://developers.google.com/maps/documentation/places/web-service/place-types */
  type?: string
  /**
   * Metres. Turns `locationBias` from a hint into a boundary. Rejected without a
   * `locationBias`, because Google would otherwise run the query worldwide.
   */
  restrictRadius?: number
}

export type ExploreNearbyParams = {
  location: [number, number]
  /** Metres. Defaults to 25000 server-side; the schema rejects above 50000. */
  radius?: number
  /** Defaults to 20, which is both Google's page maximum and the schema's ceiling. */
  limit?: number
}

export async function getExploreItems(
  params: ExploreParams,
  accessToken?: string,
): Promise<GetExploreItemsResponse['result']> {
  const queryParams = new URLSearchParams(
    onlyDefinedProperties({
      query: params.query,
      locationBias: params.locationBias.join(','),
      type: params.type,
      restrictRadius: params.restrictRadius?.toString(),
    }),
  )
  const uri = `${endpointUri}?${queryParams.toString()}`
  return request
    .get<GetExploreItemsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export async function getExploreNearbyItems(
  params: ExploreNearbyParams,
  accessToken?: string,
): Promise<GetExploreNearbyItemsResponse['result']> {
  const queryParams = new URLSearchParams(
    onlyDefinedProperties({
      location: params.location.join(','),
      radius: params.radius?.toString(),
      limit: params.limit?.toString(),
    }),
  )
  const uri = `${nearbyEndpointUri}?${queryParams.toString()}`
  return request
    .get<GetExploreNearbyItemsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}
